import * as _ from 'lodash-es';
import Quill from 'quill/core';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ContentPreviewPopOver from '../components/ContentPreviewPopOver';
import Link from '../editor/formats/Link';
import Mention from '../editor/formats/Mention';
import ExternalPostDetailPreview from '../editor/mentions/ExternalPostDetailPreview';
import type { Mention as MentionType } from '../editor/types';
import { findQuillByNode } from '../editor/utils/findByNode';
import matchEntityLink from '../editor/utils/matchEntityLink';
import { parseMentionId } from '../editor/utils/matchMention';
import GroupDetailPreview from '../groups/GroupDetailPreview';
import IntegrationDetailPreview from '../integrations/IntegrationDetailPreview';
import TopicDetailPreview from '../topic/TopicDetailPreview';
import UserDetailPreview from '../user/UserDetailPreview';
import useHover from '../utils/useHover';
import * as styles from './HoverContentPreview.module.scss';
import PostDetailPreview from './PostDetailPreview';
import { ENTITY_CARD_HOVER_GROUP_ID } from './constants';

type RenderPreviewOptions = {
  hoveredEntity: HoveredEntity;
  handlePreviewLoadChange: (loaded: boolean) => void;
  handleMinHeightChange: (newMinHeight: number | null) => void;
};

const renderPreview = ({
  hoveredEntity,
  handlePreviewLoadChange,
  handleMinHeightChange,
}: RenderPreviewOptions) => {
  if (hoveredEntity.type === 'post') {
    return (
      <PostDetailPreview
        postId={hoveredEntity.id}
        anchor={hoveredEntity.anchor}
        params={hoveredEntity.params}
        open
        onPreviewLoadChange={handlePreviewLoadChange}
      />
    );
  }

  if (hoveredEntity.type === 'external-post') {
    return (
      <ExternalPostDetailPreview
        id={hoveredEntity.id}
        onPreviewLoadChange={handlePreviewLoadChange}
      />
    );
  }

  if (hoveredEntity.type === 'group') {
    return (
      <GroupDetailPreview
        groupId={hoveredEntity.id}
        setMinHeight={handleMinHeightChange}
      />
    );
  }
  if (hoveredEntity.type === 'topic') {
    return (
      <TopicDetailPreview
        topicId={hoveredEntity.id}
        setMinHeight={handleMinHeightChange}
      />
    );
  }
  if (hoveredEntity.type === 'user') {
    return (
      <UserDetailPreview
        userId={hoveredEntity.id}
        setMinHeight={handleMinHeightChange}
      />
    );
  }
  return (
    <IntegrationDetailPreview
      id={
        hoveredEntity.anchor
          ? `${hoveredEntity.id}#${hoveredEntity.anchor}`
          : hoveredEntity.id
      }
      type={hoveredEntity.type}
      onPreviewLoadChange={handlePreviewLoadChange}
    />
  );
};

const HOVER_PREVIEW_BOUNDARY_ATTRIBUTE = 'data-hover-preview-boundary';
const HOVERABLE_ATTRIBUTE = 'data-hoverable';

function findQuillByHoveredNode(domNode: Element): Quill | null {
  const quillContainer = domNode.closest('.ql-container');
  return quillContainer ? findQuillByNode(quillContainer) : null;
}

function isSelectedLinkInQuill(target: Element) {
  const quill = findQuillByHoveredNode(target);
  if (!quill) return false;
  const selection = quill.getSelection();
  if (!selection) return false;

  const [leaf] = quill.getLeaf(selection.index);
  return leaf?.parent.domNode === target;
}

function isTargetInsideBoundary(target: Element) {
  return target.closest(`[${HOVER_PREVIEW_BOUNDARY_ATTRIBUTE}]`) != null;
}

interface HoveredEntity {
  domNode: Element;
  type: string;
  id: string;
  anchor: string | undefined;
  params?: string;
}

const getHoveredEntity = (element: Element): HoveredEntity | null => {
  if (isTargetInsideBoundary(element)) {
    return null;
  }

  const hoverableElement = element.closest(`[${HOVERABLE_ATTRIBUTE}]`);
  if (hoverableElement instanceof HTMLElement) {
    const type = hoverableElement.getAttribute('data-type');
    const id = hoverableElement.getAttribute('data-id');
    if (type && id) {
      return {
        domNode: hoverableElement,
        type,
        id,
        anchor: undefined,
      };
    }
  } else if (
    element.classList.contains(Link.className) &&
    !isSelectedLinkInQuill(element)
  ) {
    const href = element instanceof HTMLElement ? Link.formats(element) : null;
    if (href) {
      const matchedLink = matchEntityLink(href);
      const { id, anchor } = matchedLink
        ? parseMentionId(matchedLink.id)
        : { id: undefined, anchor: undefined };

      if (matchedLink && id) {
        return {
          domNode: element,
          type: matchedLink.type,
          id,
          anchor,
        };
      }
    }
  } else {
    const closestMention = element.closest<HTMLElement>(
      `.${Mention.className}`,
    );

    if (closestMention) {
      const value = Mention.value(closestMention) as MentionType;
      const { type, params } = value;

      const { id, anchor } = parseMentionId(value.id);

      if (type && id != null && !element.classList.contains('ql-selected')) {
        return {
          domNode: closestMention,
          type,
          id,
          anchor,
          params,
        };
      }
    }
  }
  return null;
};

class HoveredEntityIdManager {
  private idToHoveredEntityMap = new Map<string, HoveredEntity>();
  private nodeToIdMap = new Map<Element, string>();

  getOrCreateId(hoveredEntity: HoveredEntity) {
    const existingId = this.nodeToIdMap.get(hoveredEntity.domNode);
    if (existingId) {
      this.idToHoveredEntityMap.set(existingId, hoveredEntity);
      return existingId;
    }

    const id = _.uniqueId('hover-item-');
    this.nodeToIdMap.set(hoveredEntity.domNode, id);
    this.idToHoveredEntityMap.set(id, hoveredEntity);
    return id;
  }

  getHoveredEntity(id: string) {
    return this.idToHoveredEntityMap.get(id);
  }
}

type HoverContentPreviewState = {
  activeHoveredEntity: HoveredEntity | null;
  isOpen: boolean;
  isPreviewLoaded: boolean;
  minHeight: number | null;
};
interface HoverContentPreviewProps {
  children: React.ReactNode;
}

const HoverContentPreview = ({ children }: HoverContentPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeIdManagerRef = useRef<HoveredEntityIdManager | null>(null);

  const getNodeIdManager = () => {
    if (!nodeIdManagerRef.current) {
      nodeIdManagerRef.current = new HoveredEntityIdManager();
    }
    return nodeIdManagerRef.current;
  };

  const [
    { activeHoveredEntity, isOpen, isPreviewLoaded, minHeight },
    setState,
  ] = useState<HoverContentPreviewState>({
    isOpen: false,
    isPreviewLoaded: false,
    activeHoveredEntity: null,
    minHeight: null,
  });

  const {
    onMouseEnter: originalOnMouseEnter,
    onMouseLeave: originalOnMouseLeave,
    setHover,
  } = useHover({
    hoverGroupId: ENTITY_CARD_HOVER_GROUP_ID,
    hoverDelay: 'slow',
    onHoverChange(currentId) {
      const nextActiveHoveredEntity =
        currentId == null
          ? null
          : getNodeIdManager().getHoveredEntity(currentId);

      setState({
        activeHoveredEntity: nextActiveHoveredEntity ?? null,
        isPreviewLoaded: false,
        isOpen: !!nextActiveHoveredEntity,
        minHeight: null,
      });
    },
  });

  const lastEnteredIdRef = useRef<string | null>(null);
  const onMouseEnter: typeof originalOnMouseEnter = (id, event) => {
    originalOnMouseEnter(id, event);
    lastEnteredIdRef.current = id;
  };

  const onMouseLeave = () => {
    if (lastEnteredIdRef.current) {
      originalOnMouseLeave(lastEnteredIdRef.current);
      lastEnteredIdRef.current = null;
    }
  };

  useEffect(() => {
    const domNode = activeHoveredEntity?.domNode;
    const container = containerRef.current;
    if (!container || !domNode) return undefined;

    const observer = new MutationObserver(() => {
      if (!domNode.isConnected) {
        setHover(null);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [activeHoveredEntity, setHover]);

  const domNode = activeHoveredEntity?.domNode;

  useEffect(() => {
    if (!domNode) return undefined;
    const quill = findQuillByHoveredNode(domNode);
    if (!quill) return undefined;

    const handleSelectionChange = () => setHover(null);

    quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);
    return () => {
      quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
    };
  }, [domNode, setHover]);

  const anchorRef = useMemo(() => {
    return {
      current: domNode ?? null,
    };
  }, [domNode]);

  const handlePreviewLoadChange = (loaded: boolean) => {
    setState((state) => ({ ...state, isPreviewLoaded: loaded }));
  };

  const handleMinHeightChange = useCallback((newMinHeight: number | null) => {
    setState((state) => ({
      ...state,
      minHeight: newMinHeight,
      isPreviewLoaded: true,
    }));
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        onMouseLeave={onMouseLeave}
        onMouseOver={({ clientX, clientY, target }) => {
          const hoveredEntity =
            target instanceof Element ? getHoveredEntity(target) : null;

          if (hoveredEntity) {
            onMouseEnter(getNodeIdManager().getOrCreateId(hoveredEntity), {
              clientX,
              clientY,
              target: hoveredEntity.domNode,
            });
          } else {
            onMouseLeave();
          }
        }}
      >
        {children}
      </div>
      <ContentPreviewPopOver
        hideOnMobile
        hidden={!isPreviewLoaded}
        allowCrossAxisFlip
        minWidth={250}
        minHeight={minHeight ?? 94}
        anchorRef={anchorRef}
        hoverDisabled
        popOver={
          isOpen && activeHoveredEntity ? (
            <div
              className={styles.popOverContainer}
              onMouseLeave={onMouseLeave}
              onMouseEnter={onMouseEnter.bind(
                null,
                getNodeIdManager().getOrCreateId(activeHoveredEntity),
              )}
            >
              <Fragment
                // Ensure preview components will update isPreviewLoaded state
                key={getNodeIdManager().getOrCreateId(activeHoveredEntity)}
              >
                {renderPreview({
                  hoveredEntity: activeHoveredEntity,
                  handleMinHeightChange,
                  handlePreviewLoadChange,
                })}
              </Fragment>
            </div>
          ) : null
        }
        open={isOpen}
      />
    </>
  );
};

HoverContentPreview.DisableHoverBoundary = ({
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...{ [HOVER_PREVIEW_BOUNDARY_ATTRIBUTE]: true }} {...rest}>
    {children}
  </div>
);

HoverContentPreview.Hoverable = ({
  type,
  id,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { type: string; id: string }) => (
  <div
    {...{ [HOVERABLE_ATTRIBUTE]: true }}
    {...rest}
    data-type={type}
    data-id={id}
  >
    {children}
  </div>
);

export default HoverContentPreview;
